<template>
  <ListItem
    :to="{
      name: 'couponEdit',
      params: { id: coupon.id },
    }"
  >
    <div class="basis-[50%] space-y-1">
      <p class="font-mono text-xs font-semibold">{{ coupon.code }}</p>
      <p>
        <UserIcon class="inline-icon text-obsidian-500" />
        {{
          coupon.customerData
            ? `${coupon.customerData.firstName} ${coupon.customerData.lastName}`
            : '-'
        }}
      </p>
    </div>

    <div class="basis-[25%]">
      <template v-if="coupon.type === CouponType.PROMO">
        <p class="font-bold">{{ t('extraKmValue', { km: coupon.benefits?.EXTRA_KM ?? 0 }) }}</p>
        <p class="text-sm text-obsidian-500">
          {{
            coupon.maxRedemptions != null
              ? t('redemptionsWithMax', {
                  count: coupon.redemptionCount ?? 0,
                  max: coupon.maxRedemptions,
                })
              : t('redemptions', { count: coupon.redemptionCount ?? 0 })
          }}
        </p>
      </template>
      <template v-else>
        <p class="font-bold">
          {{ formatCurrency(coupon.remainingValue ?? 0) }}
          <span class="font-normal text-obsidian-500">/</span>
        </p>
        <p>{{ formatCurrency(coupon.originalValue ?? 0) }}</p>
      </template>
    </div>

    <div class="basis-[20%]">
      <div class="flex flex-wrap gap-2">
        <Badge :variant="statusVariant">
          {{
            coupon.isActive ? (isCouponExpired(coupon) ? t('expired') : t('active')) : t('inactive')
          }}
        </Badge>
        <Badge variant="neutral">
          {{ t(`couponType.${coupon.type}`) }}
        </Badge>
      </div>
    </div>

    <div class="flex basis-[5%] justify-end">
      <DropdownDotted :buttons="buttons" />
    </div>
  </ListItem>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { Coupon } from '@/entities/coupons/coupon.entity';
import { formatCurrency } from '@/utils/format-numbers';
import { couponsService } from '@/api/coupons.service';
import DropdownDotted from '@/components/DropdownDotted.vue';
import { computed } from 'vue';
import { DateTime } from 'luxon';
import { UserIcon } from 'lucide-vue-next';
import ListItem from '@/components/ListItem.vue';
import Badge from '@/components/Badge.vue';
import { usePdfPreview } from '@/hooks/use-pdf-preview';
import { downloadBlob } from '@/utils/download-file';
import { CouponType } from '@carvia/ros-client-types';
import type { BadgeVariant } from '@carvia/theme/lib/cva/badge';

const props = defineProps<{
  coupon: Coupon;
}>();

const { t } = useI18n();
const pdfPreview = usePdfPreview();

const statusVariant = computed<BadgeVariant>(() =>
  props.coupon.isActive && !isCouponExpired(props.coupon) ? 'success' : 'problem',
);

const downloadPdf = async (couponId: string) => {
  await pdfPreview.openPdfPreview({
    title: t('couponPdfTitle', { code: props.coupon.code }),
    fileName: `coupon-${couponId}.pdf`,
    load: () => couponsService.downloadPdf(couponId),
    errorMessage: t('couldNotLoadPdf'),
  });
};

const downloadPdfDirect = async (couponId: string) => {
  const blob = await couponsService.downloadPdf(couponId);
  await downloadBlob(blob, `coupon-${couponId}.pdf`);
};

const buttons = computed(() => {
  if (props.coupon.type === CouponType.PROMO) {
    return [];
  }

  return [
    {
      title: t('downloadPdf'),
      onClick: () => downloadPdf(props.coupon.id),
    },
    {
      title: t('downloadPdfDirect'),
      onClick: () => downloadPdfDirect(props.coupon.id),
    },
  ];
});

function isCouponExpired(coupon: Coupon) {
  return coupon.validTo && DateTime.fromISO(coupon.validTo).toMillis() < DateTime.now().toMillis();
}
</script>

<i18n lang="json">
{
  "en": {
    "active": "Active",
    "inactive": "Inactive",
    "expired": "Expired",
    "editCoupon": "Edit coupon",
    "downloadPdf": "Show PDF",
    "downloadPdfDirect": "Download PDF",
    "couponPdfTitle": "Coupon {code}",
    "couldNotLoadPdf": "The PDF could not be loaded. Please try again.",
    "extraKmValue": "{km} km free",
    "redemptions": "{count} redemptions",
    "redemptionsWithMax": "{count} / {max} redemptions"
  },
  "de": {
    "active": "Aktiv",
    "inactive": "Inaktiv",
    "expired": "Abgelaufen",
    "editCoupon": "Gutschein bearbeiten",
    "downloadPdf": "PDF anzeigen",
    "downloadPdfDirect": "PDF herunterladen",
    "couponPdfTitle": "Gutschein {code}",
    "couldNotLoadPdf": "Das PDF konnte nicht geladen werden. Bitte versuche es erneut.",
    "extraKmValue": "{km} km gratis",
    "redemptions": "{count} Einlösungen",
    "redemptionsWithMax": "{count} / {max} Einlösungen"
  },
  "es": {
    "active": "Activo",
    "inactive": "Inactivo",
    "expired": "Caducado",
    "editCoupon": "Editar cupón",
    "downloadPdf": "Ver PDF",
    "downloadPdfDirect": "Descargar PDF",
    "couponPdfTitle": "Cupón {code}",
    "couldNotLoadPdf": "No se pudo cargar el PDF. Inténtelo de nuevo.",
    "extraKmValue": "{km} km gratis",
    "redemptions": "{count} redenciones",
    "redemptionsWithMax": "{count} / {max} redenciones"
  }
}
</i18n>
